// Company stores (S3, 2026-10-12; S4 shop 2026-10-12; "From" 2026-10-19) —
// /store: the company's collection. Each card opens the product page
// (colours, sizes, a live price from the server). The cards' "From $X each"
// comes from the server too (GET /portal/store/prices, asked after the
// grid shows): a product it couldn't price shows no price — we never show a
// number the server didn't give. Store employees and the store's admin
// land here.
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PortalLayout } from "@/components/PortalLayout";
import { StoreGate, StoreHeading } from "@/components/StoreGate";
import { StoreProductGrid } from "@/components/StoreProductCard";
import { LINK, useMyCart } from "@/components/StoreShopUi";
import { storeOffMessage, type MyStore } from "@/lib/portal-store";
import { cartPieces } from "@/lib/store-cart";
import { fetchFromPrices, STORE_FROM_PRICES_KEY } from "@/lib/store-shop";

export function StorePage() {
  return (
    <PortalLayout>
      <StoreGate>
        {({ store, products }) => (
          <>
            <StoreHeading store={store} />
            {storeOffMessage(store, "browse") && (
              <p className="mb-6 rounded-md border border-rule bg-warn-bg px-4 py-3 text-sm text-warn">{storeOffMessage(store, "browse")}</p>
            )}
            <CartLine />
            {products.length > 0 ? (
              <PricedGrid store={store} products={products} />
            ) : (
              <p className="rounded-lg border border-rule bg-white p-6 text-sm text-brown-400">
                There's nothing in the store yet. Check back soon.
              </p>
            )}
          </>
        )}
      </StoreGate>
    </PortalLayout>
  );
}

/** The grid, with each product's "From" price once the server has it. A
 *  paused store prices nothing (ordering is off), so it asks for none. */
function PricedGrid({ store, products }: Pick<MyStore, "store" | "products">) {
  const prices = useQuery({
    queryKey: STORE_FROM_PRICES_KEY,
    queryFn: fetchFromPrices,
    enabled: store.status === "live",
    staleTime: 5 * 60_000,
    retry: false,
  });
  const byId = useMemo(() => new Map((prices.data ?? []).map((p) => [p.productId, p])), [prices.data]);
  return <StoreProductGrid products={products} shop prices={byId} wide />;
}

function CartLine() {
  const { cart } = useMyCart();
  const n = cartPieces(cart);
  if (n === 0) return null;
  return (
    <p className="mb-6 text-sm text-brown-400">
      {n.toLocaleString("en-US")} {n === 1 ? "piece" : "pieces"} in your cart.{" "}
      <Link to="/store/cart" className={LINK}>
        Check out
      </Link>
    </p>
  );
}
